import { styled } from "goober";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { publicRouteUrl } from "../../../shared/publicUrl";
import { apiUrl, publicDomain, publicUrl } from "../../lib/tenantOrigin";
import type { LiveFeature, LiveFeatureApi } from "./feature";

// Routing the frame through verify-route mints the space-scoped
// `__3b_session` cookie on the page's origin before it loads, so the
// page's same-origin requests arrive authenticated — matching how the
// page behaves when served directly.
function sessionHandshakeUrl(
  redirect: string,
  spaceId: string,
  externalApiUrl: string
): string {
  const url = new URL("/auth/verify-route", externalApiUrl);
  url.searchParams.set("scope", "space");
  url.searchParams.set("spaceId", spaceId);
  url.searchParams.set("redirect", redirect);
  return url.toString();
}

const StyledIframe = styled("iframe")`
  display: block;
  width: 100%;
  height: 100%;
  border: none;
  background: var(--surface-primary);
`;

// allow-same-origin with allow-scripts nullifies the sandbox attribute as a
// boundary; the real isolation here is the origin split (space origin vs UI
// origin), identical to the page open in its own tab. The attribute's only
// residual effect is blocking popup sandbox escape.
const sandbox =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-downloads";

type StepPageProps = {
  spaceSlug: string;
  spaceId: string | null;
  branchId: string | null;
  publicRoute: string;
  externalId: string | null;
  features?: readonly LiveFeature[];
};

// The step's page, live in the virtual browser: the iframe points at the
// route as actually served, so what the user sees is what the origin
// returns right now. Feature attach (inspect / agent control / URL
// reporting) receives a MessagePort posted by the runtime bundle that the
// public service injects when the request carries a valid signed
// `__3b_live_browser_runtime` cookie (minted during the `__3b_auth`
// exchange when the frame is embedded in our UI).
export function Page({
  spaceSlug,
  spaceId,
  branchId,
  publicRoute,
  externalId,
  features = [],
}: StepPageProps) {
  const externalPublicUrl = publicUrl();
  const externalApiUrl = apiUrl();
  const domain = publicDomain();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const portRef = useRef<MessagePort | null>(null);
  const cleanupsRef = useRef<(() => void)[]>([]);
  const featuresRef = useRef(features);
  featuresRef.current = features;

  // Each document's runtime posts one port when its `<script defer>`
  // runs. On each new port we tear down the previous document's features
  // and attach the new document's; we intentionally do NOT tear down on
  // `iframe.load`, because `load` and the new port's arrival race and we
  // can't distinguish "load fires first" from "port arrives first" cross-
  // origin. Using the new port as the boundary makes the ordering moot.
  //
  // Attacker code in the same document can still post a second port
  // *after* our runtime's; we'd tear the real port down and adopt the
  // impostor. Defence against that would need a cross-origin
  // navigation-start signal (which the platform doesn't offer); out of
  // scope for a user's own workspace, where every script in the frame is
  // trusted-by-user.

  const src = useMemo(
    () =>
      publicRouteUrl(
        spaceSlug,
        publicRoute,
        {
          publicUrl: externalPublicUrl,
          publicWildcardDomain: domain.wildcardDomain,
          combinedSpaceTenantLabel: domain.combinedSpaceTenantLabel,
        },
        { branchId, externalId }
      ),
    [spaceSlug, publicRoute, branchId, externalId, externalPublicUrl, domain]
  );

  const previewOrigin = useMemo(() => new URL(src).origin, [src]);

  const finalSrc = spaceId
    ? sessionHandshakeUrl(src, spaceId, externalApiUrl)
    : src;

  useLayoutEffect(() => {
    if (iframeRef.current) iframeRef.current.src = finalSrc;
  }, [finalSrc]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type !== "3b-preview-port") return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      if (e.origin !== previewOrigin) return;
      const port = e.ports[0];
      if (!port) return;

      // Tear down whatever was attached for the previous document.
      for (const cleanup of cleanupsRef.current) cleanup();
      cleanupsRef.current = [];
      portRef.current?.close();

      portRef.current = port;
      port.start();

      const api: LiveFeatureApi = {
        iframeWindow: iframeRef.current?.contentWindow ?? null,
        port,
      };
      for (const feature of featuresRef.current) {
        const cleanup = feature.attach?.(api);
        if (cleanup) cleanupsRef.current.push(cleanup);
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      for (const cleanup of cleanupsRef.current) cleanup();
      cleanupsRef.current = [];
      portRef.current?.close();
    };
  }, [previewOrigin]);

  return <StyledIframe ref={iframeRef} sandbox={sandbox} />;
}
